import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { clearApiCache } from '../services/apiService';

// Default year — hardcoded to 2026 per project convention.
// Individual pages provide their own year selectors.
const CURRENT_YEAR = '2026';

// Available years for per-page selectors
const _calendarYear = new Date().getFullYear();
const AVAILABLE_YEARS = Array.from(
  new Set([
    '2026',
    _calendarYear.toString(),
    ...Array.from({ length: 12 }, (_, i) => (2026 - i).toString())
  ])
).sort((a, b) => b - a);

// LocalStorage key for persisting selected year
const STORAGE_KEY = 'the-cycle-selected-year';

/**
 * Read persisted year from localStorage, validate it's in AVAILABLE_YEARS.
 */
function getPersistedYear() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && AVAILABLE_YEARS.includes(stored)) {
      return stored;
    }
  } catch {
    // localStorage may be unavailable (SSR, privacy mode)
  }
  return CURRENT_YEAR;
}

const YearContext = createContext();

export const YearProvider = ({ children }) => {
  const [selectedYear, setSelectedYear] = useState(() => getPersistedYear());

  // Persist year changes to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, selectedYear);
    } catch {
      // Silently ignore storage errors
    }
  }, [selectedYear]);

  // Validated setter — only accept years in AVAILABLE_YEARS
  const setYear = useCallback((yr) => {
    const yearStr = String(yr);
    if (AVAILABLE_YEARS.includes(yearStr)) {
      setSelectedYear(yearStr);
      // Bust the in-memory API cache so dependent pages refetch with the new year
      // instead of serving 60s-stale data from the previous year.
      try { clearApiCache(); } catch { /* no-op */ }
    }
  }, []);

  const value = useMemo(() => ({
    year: selectedYear,
    setYear,
    currentYear: CURRENT_YEAR,
    availableYears: AVAILABLE_YEARS,
    isCurrentYear: selectedYear === CURRENT_YEAR,
  }), [selectedYear, setYear]);

  return (
    <YearContext.Provider value={value}>
      {children}
    </YearContext.Provider>
  );
};

/**
 * Hook to access the global year context.
 * Returns { year, setYear, currentYear, availableYears, isCurrentYear }
 */
export const useYear = () => {
  const ctx = useContext(YearContext);
  if (!ctx) {
    // Fallback for components rendered outside provider — return current year
    return {
      year: CURRENT_YEAR,
      setYear: () => {},
      currentYear: CURRENT_YEAR,
      availableYears: AVAILABLE_YEARS,
      isCurrentYear: true,
    };
  }
  return ctx;
};

export { CURRENT_YEAR, AVAILABLE_YEARS };
export default YearContext;
